/**
 * 文案卡片组件
 * 支持选中、编辑标题/正文/标签
 */

import { useState } from 'react'
import { Copy, Check, Edit2, X, Save } from 'lucide-react'
import type { GeneratedNote } from '@/hooks/useCopywriting'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'

interface CopywritingNoteCardProps {
  note: GeneratedNote
  index: number
  onUpdate: (id: string, updates: Partial<Pick<GeneratedNote, 'title' | 'content' | 'hashtags'>>) => void
  onToggleSelection: (id: string) => void
}

export function CopywritingNoteCard({
  note,
  index,
  onUpdate,
  onToggleSelection,
}: CopywritingNoteCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(note.title)
  const [editContent, setEditContent] = useState(note.content)
  const [editHashtags, setEditHashtags] = useState(note.hashtags?.join(', ') || '')
  const [copied, setCopied] = useState(false)

  // 复制文案
  const handleCopy = () => {
    const text = `${note.title}\n\n${note.content}\n\n${(note.hashtags || []).map((t: string) => `#${t}`).join(' ')}`
    navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success('已复制到剪贴板')
    setTimeout(() => setCopied(false), 2000)
  }

  // 开始编辑
  const handleStartEdit = () => {
    setEditTitle(note.title)
    setEditContent(note.content)
    setEditHashtags((note.hashtags || []).join(', '))
    setIsEditing(true)
  }

  // 保存编辑
  const handleSaveEdit = () => {
    const hashtags = editHashtags
      .split(/[,，]/)
      .map((t: string) => t.trim().replace(/^#/, ''))
      .filter(Boolean)

    onUpdate(note.id, {
      title: editTitle,
      content: editContent,
      hashtags,
    })
    setIsEditing(false)
    toast.success('修改已保存')
  }

  // 取消编辑
  const handleCancelEdit = () => {
    setIsEditing(false)
  }

  return (
    <div
      className={`
        relative p-4 border rounded-lg transition-all
        ${note.selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
          : 'border-border hover:border-primary/50'
        }
      `}
    >
      {/* 顶部：序号 + 选中框 + 操作按钮 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Checkbox
            id={`note-${note.id}`}
            checked={note.selected}
            disabled={!!note.savedNoteId}
            onCheckedChange={(checked) => {
              // 只有当新状态与当前状态不同时才触发
              if (checked !== note.selected) {
                onToggleSelection(note.id)
              }
            }}
          />
          <label
            htmlFor={`note-${note.id}`}
            className="text-sm font-medium text-muted-foreground cursor-pointer select-none"
            onClick={(e) => {
              // 防止label点击与checkbox点击重复触发
              e.preventDefault()
              if (!note.savedNoteId) {
                onToggleSelection(note.id)
              }
            }}
          >
            文案 {index + 1}
          </label>
          {note.savedNoteId && (
            <Badge variant="secondary" className="text-xs">
              已保存
            </Badge>
          )}
          {note.selected && !note.savedNoteId && (
            <Badge variant="default" className="text-xs">
              已选中
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1">
          {isEditing ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSaveEdit}
                className="h-8 w-8 text-green-600 hover:text-green-700"
              >
                <Save className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancelEdit}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleStartEdit}
                className="h-8 w-8"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCopy}
                className="h-8 w-8"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 内容区域 */}
      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">标题</label>
            <Input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="font-bold text-lg border-none shadow-none focus-visible:ring-1 px-0"
              placeholder="请输入标题"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">正文</label>
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[200px] resize-none border-none shadow-none focus-visible:ring-1 px-0 text-[15px] leading-relaxed"
              placeholder="请输入正文"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              标签（用逗号分隔）
            </label>
            <Input
              value={editHashtags}
              onChange={(e) => setEditHashtags(e.target.value)}
              placeholder="标签1, 标签2, 标签3"
              className="border-none shadow-none focus-visible:ring-1 px-0 text-blue-600"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="ghost" onClick={handleCancelEdit}>取消</Button>
            <Button size="sm" onClick={handleSaveEdit}>保存</Button>
          </div>
        </div>
      ) : (
        <div 
          className="space-y-3 cursor-pointer group/content" 
          onClick={handleStartEdit}
        >
          {/* 标题 */}
          <h4 className="font-bold text-lg leading-tight group-hover/content:text-primary/80 transition-colors">
            {note.title}
          </h4>

          {/* 正文 - 模拟小红书排版 (约22字/行) */}
          <div className="max-w-[24em]">
            <p className="text-[#333] whitespace-pre-wrap text-[15px] leading-relaxed">
              {note.content}
            </p>
          </div>

          {/* 标签 */}
          <div className="flex flex-wrap gap-1.5 pt-2">
            {(note.hashtags || []).map((tag: string, i: number) => (
              <span key={i} className="text-[13px] text-[#13386c] hover:underline">
                #{tag}
              </span>
            ))}
          </div>
          
          <div className="opacity-0 group-hover/content:opacity-100 transition-opacity text-xs text-muted-foreground text-center pt-2">
            (点击修改)
          </div>
        </div>
      )}
    </div>
  )
}

